import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../AuthContext';
import VideoGenerationDashboard from './VideoGenerationDashboard';
import LanguageSelectorWithVideoGeneration from './LanguageSelectorWithVideoGeneration';
import WebhookMonitoringDashboard from './WebhookMonitoringDashboard';

const VideoManagementAdmin = () => {
  const { authState } = useContext(AuthContext);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Initialize component with authenticated user
  useEffect(() => {
    if (authState.user) {
      // Get saved language preference
      const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
      setSelectedLanguage(savedLanguage);
      setLoading(false);
    } else if (!authState.loading) {
      setLoading(false);
    }
  }, [authState]);

  const handleLanguageChange = (newLanguage) => {
    setSelectedLanguage(newLanguage);
    localStorage.setItem('selectedLanguage', newLanguage);
  };

  if (authState.loading || loading) {
    return (
      <div className="video-management-loading">
        <div className="loading-spinner"></div>
        <p>Loading Video Management...</p>
      </div>
    );
  }

  if (!authState.user) {
    return (
      <div className="video-management-error">
        <h2>❌ Access Denied</h2>
        <p>You need to be logged in to access the Video Management dashboard.</p>
      </div>
    );
  }

  if (authState.user.role !== 1) {
    return (
      <div className="video-management-error">
        <h2>🚫 Unauthorized</h2>
        <p>Only administrators can access the Video Management dashboard.</p>
      </div>
    );
  }

  return (
    <div className="video-management-admin main-container">
      <div className="admin-header">
        <div className="header-content">
          <h1>🎬 Video Management Center</h1>
          <div className="user-info">
            <span className="welcome-text">Welcome, {authState.user.company_name || authState.user.name || 'Admin'}</span>
            <span className="user-id">ID: {authState.user._id}</span>
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="nav-tabs">
          <button 
            className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Video Dashboard
          </button>
          <button 
            className={`nav-tab ${activeTab === 'language' ? 'active' : ''}`}
            onClick={() => setActiveTab('language')}
          >
            🌍 Language & Generation
          </button>
          <button 
            className={`nav-tab ${activeTab === 'webhooks' ? 'active' : ''}`}
            onClick={() => setActiveTab('webhooks')}
          >
            🔔 System Monitoring
          </button>
        </div>
      </div>

      <div className="admin-content">
        {/* Language Selector Section */}
        {activeTab === 'language' && (
          <div className="section language-section">
            <div className="section-header">
              <h2>🌍 Language & Video Generation</h2>
              <p>Select a language to automatically generate videos for all your locations</p>
            </div>
            <LanguageSelectorWithVideoGeneration
              userId={authState.user._id}
              currentLanguage={selectedLanguage}
              onLanguageChange={handleLanguageChange}
              showVideoGeneration={true}
            />
          </div>
        )}

        {/* Dashboard Section */}
        {activeTab === 'dashboard' && (
          <div className="section dashboard-section">
            <div className="section-header">
              <h2>📊 Video Generation Dashboard</h2>
              <p>Monitor video generation progress and manage your multilanguage content</p>
            </div>
            <VideoGenerationDashboard userId={authState.user._id} />
          </div>
        )}

        {/* System Monitoring Section */}
        {activeTab === 'webhooks' && (
          <div className="section webhook-section">
            <div className="section-header">
              <h2>🔔 System Monitoring</h2>
              <p>Monitor cron job status and track video generation progress in real-time</p>
            </div>
            <WebhookMonitoringDashboard />
          </div>
        )}
      </div>

      <style jsx>{`
        .video-management-admin {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .admin-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding: 20px 0 0 0;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px 20px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .nav-tabs {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          gap: 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .nav-tab {
          padding: 12px 24px;
          background: transparent;
          border: none;
          color: #666;
          cursor: pointer;
          transition: all 0.3s ease;
          border-bottom: 3px solid transparent;
          font-weight: 500;
        }

        .nav-tab:hover {
          color: #333;
          background: rgba(255, 255, 255, 0.5);
        }

        .nav-tab.active {
          color: #007bff;
          border-bottom-color: #007bff;
          background: rgba(0, 123, 255, 0.1);
        }

        .admin-header h1 {
          margin: 0;
          color: #333;
          font-size: 32px;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .user-info {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
        }

        .welcome-text {
          font-weight: 600;
          color: #333;
          font-size: 16px;
        }

        .user-id {
          font-size: 12px;
          color: #666;
          font-family: monospace;
        }

        .admin-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 30px 20px;
        }

        .section {
          margin-bottom: 40px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 15px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .section-header {
          padding: 25px 30px;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .section-header h2 {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 24px;
          font-weight: 600;
        }

        .section-header p {
          margin: 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        }

        .language-section .section-header {
          background: linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(23, 162, 184, 0.1) 100%);
        }

        .dashboard-section {
          background: transparent;
          box-shadow: none;
        }

        .webhook-section {
          background: transparent;
          box-shadow: none;
        }

        .webhook-section .section-header {
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%);
        }

        .video-management-loading,
        .video-management-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          text-align: center;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(255, 255, 255, 0.3);
          border-top: 4px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .video-management-error h2 {
          margin: 0 0 15px 0;
          font-size: 28px;
        }

        .video-management-error p {
          margin: 0;
          font-size: 16px;
          opacity: 0.9;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 15px;
            text-align: center;
          }

          .admin-header h1 {
            font-size: 24px;
          }

          .user-info {
            align-items: center;
          }

          .nav-tabs {
            flex-wrap: wrap;
            justify-content: center;
          }

          .nav-tab {
            padding: 10px 16px;
            font-size: 14px;
          }

          .admin-content {
            padding: 20px 15px;
          }

          .section-header {
            padding: 20px;
          }

          .section-header h2 {
            font-size: 20px;
          }
        }

        @media (max-width: 480px) {
          .admin-header h1 {
            font-size: 20px;
          }

          .section-header h2 {
            font-size: 18px;
          }

          .welcome-text {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default VideoManagementAdmin;